import { type TFunction } from 'i18next'
import { type LiteralUnion } from 'type-fest'

import { type PathKey } from '~/apps/ninesegsAnalyzer/routes/paths'
import { type CustomerAttribute } from '~/hooks/useSurveyQuery'
import {
  type DiffKey,
  type DiffValue,
  findByPath,
  type FiveSegment,
  type FiveSegmentIndicator,
  fiveSegments,
  getDiffText,
  type NineSegment,
  type NineSegmentIndicator,
  nineSegments,
} from '~/utils'

export const pathKey = 'customerAttributeAnalysis' satisfies PathKey

export type ScrollHash = 'table'

export const allKey = 'all'
export type AllKey = typeof allKey
const allId = '__TOTAL__'

type DiffItem = LiteralUnion<AllKey, CustomerAttribute['id']>

export type CountColumnKey = LiteralUnion<AllKey, CustomerAttribute['id']>
export type DiffColumnKey = DiffKey<DiffItem>
export type ColumnKey = LiteralUnion<
  'category' | 'subcategory',
  CountColumnKey | DiffColumnKey
>
export type SortColumnKey = CountColumnKey | DiffColumnKey

export type ColumnsDiff = DiffValue<DiffItem>

export const countColumnKeyToId = (countColumnKey: CountColumnKey) =>
  countColumnKey === allKey ? allId : countColumnKey

export const getCountColumnLabel = (
  columnCustomerAttributes: CustomerAttribute[],
  countColumnKey: CountColumnKey,
  t: TFunction,
) =>
  countColumnKey === allKey
    ? t('overall')
    : (findByPath(columnCustomerAttributes, 'id', countColumnKey)?.name ??
      t('unknown_customized_segments'))

export const getDiffColumnLabel = (
  columnCustomerAttributes: CustomerAttribute[],
  columnsDiff: ColumnsDiff,
  t: TFunction,
  separator?: string,
): string =>
  getDiffText(columnsDiff, {
    textFn: (item) => getCountColumnLabel(columnCustomerAttributes, item, t),
    separator,
  })

export type DisplayRowId = LiteralUnion<
  FiveSegment | FiveSegmentIndicator | NineSegment | NineSegmentIndicator,
  string
>

export const indicatorsForFiveSegs: (FiveSegment | FiveSegmentIndicator)[] = [
  ...fiveSegments,
  'rateLoyal',
  'rateCurrentUser',
  'rateRecognition',
  'rateCv',
  'ratioLoyalToCurrentUser',
]

export const indicatorsForNineSegs: (NineSegment | NineSegmentIndicator)[] = [
  ...nineSegments,
  'npi',
  'aNpi',
  'nNpi',
  'uNpi',
]

export const indicatorsForSummary: (
  FiveSegmentIndicator | NineSegmentIndicator
)[] = [
  'rateLoyal',
  'rateCurrentUser',
  'rateRecognition',
  'rateCv',
  'npi',
  'aNpi',
  'nNpi',
  'uNpi',
]

export type CountData = {
  counts: Record<CountColumnKey | DiffColumnKey, number>
  ratios: Record<CountColumnKey | DiffColumnKey, number>
}
